import { EDITABLE } from '../dom-selection'
import { useEditor } from '../../editor-state'
import { scopeBlocks } from '../../core/scope'
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Block } from '@w/content-blocks'
import { ulid } from '@w/utils'
import { codeHighlightingLanguages } from '@w/code-highlighting'
import {
  GripVerticalIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  Trash2Icon,
  CopyIcon,
  FileTextIcon,
  HashIcon,
  MessageSquareIcon,
  CircleAlertIcon,
  ChevronRightIcon,
  CodeXmlIcon,
  ListTodoIcon,
  ListIcon,
} from '@w/ui/components/icon'
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  MenuSeparator,
} from '@w/ui/components/menu'
import { IconButton } from '@w/ui/components/icon-button'
import { blockClass, blockRange, draggedBlocks } from '../../core/block-selection'
import { columnPercent, keyResizeColumns } from '../../core/columns'
import { convertQuestionSubtype } from '../blocks/question-subtype'
import { turnInto } from '../../core/format-block'
import type { Element } from '../../core/tree'
import { children, findBlock, plain, pointAt, rows } from '../../core/tree-read'
import { slashItems } from '../../core/slash-menu'
import type { DropPlacement } from '../../core/drop'
import type { EditorIntent } from '../../core/intents'
import type { Overlay } from '../view-api'
import {
  blockElement,
  focusedBlock,
  OverlayPortal,
  panelClass,
  segment,
  select,
  topBlock,
  useLiveApi,
  type OverlayApi,
} from './shared'

interface Drop {
  target: string
  placement: DropPlacement | 'left' | 'right'
  left: number
  top: number
  width: number
  height?: number
}
interface Drag {
  blocks: string[]
  x: number
  y: number
  label: string
}
const dragMime = 'application/x-core-blocks'

export const BlockActions: Overlay = ({
  api: editingApi,
}: {
  api: OverlayApi
}) => {
  const scope = editingApi.state.scope
  const point = scope && { blockId: scope.blockId, offset: 0 }
  const api =
    scope && point
      ? {
          ...editingApi,
          state: {
            ...editingApi.state,
            blocks: scopeBlocks(editingApi.state.doc),
            scope: undefined,
            selection: { anchor: point, focus: point, nodes: [scope.blockId] },
          },
        }
      : editingApi
  const [hovered, setHovered] = useState<string | null>()
  const [open, setOpen] = useState(false)
  const [wrapped, setWrapped] = useState<string[]>([])
  const [drag, setDrag] = useState<Drag | null>(null)
  const [drop, setDrop] = useState<Drop | null>(null)
  const [handleSize, setHandleSize] = useState({ width: 0, height: 0 })
  const handleRef = useRef<HTMLDivElement>(null)
  const live = useLiveApi(api)
  const editor = useEditor()
  const menu = useRef<HTMLDivElement>(null)
  const dragging = useRef<Drag | null>(null)
  const destination = useRef<Drop | null>(null)
  const selected = api.state.selection?.nodes
  // A list item owns its handle (Lexical `$draggableNode`): it drags alone; the menu still acts on the enclosing block.
  const focused = !api.parentEditor || !!scope || !!selected?.length
  const block =
    hovered === null
      ? null
      : (api.state.blocks.find((top) => hovered && findBlock([top], hovered)) ??
        (focused ? focusedBlock(api) : undefined))
  const hoveredItem = hovered && block ? findBlock([block], hovered) : undefined
  const listItem = hoveredItem?.type === 'li' ? hoveredItem : undefined
  const element = block ? blockElement(api, (listItem ?? block).id) : null
  useLayoutEffect(() => {
    const controls = handleRef.current
    if (!controls) return () => {}
    const measure = () => {
      const { width, height } = controls.getBoundingClientRect()
      setHandleSize((previous) =>
        previous.width === width && previous.height === height
          ? previous
          : { width, height },
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(controls)
    return () => {
      observer.disconnect()
    }
  })
  useLayoutEffect(() => {
    const projected: { element: HTMLElement; previous: string }[] = []
    for (const id of wrapped) {
      const element = blockElement(editingApi, id)
      if (!element || element.tagName !== 'PRE') continue
      projected.push({ element, previous: element.style.whiteSpace })
      element.style.whiteSpace = 'pre-wrap'
    }
    return () => {
      for (const { element, previous } of projected)
        element.style.whiteSpace = previous
    }
  }, [editingApi, wrapped])
  useEffect(() => {
    const root = api.root
    if (!root || api.mode !== 'edit') return () => {}
    let press: {
      id: number
      x: number
      y: number
      block: string
      selected: boolean
    } | null = null
    let timer: ReturnType<typeof setTimeout> | undefined
    let frame = 0
    const cancelPress = () => {
      clearTimeout(timer)
      timer = undefined
    }
    const choose = (id: string) => {
      const api = live.current
      select(api, blockRange(api.state.blocks, id, id))
      setHovered(id)
    }
    const hover = (event: MouseEvent) => {
      if (live.current.layout === 'touch' || open || dragging.current) return
      if (event.target instanceof Node && menu.current?.contains(event.target))
        return
      const block =
        event.target instanceof Node && root.contains(event.target)
          ? topBlock(live.current, event.target)
          : undefined
      if (block) {
        setHovered(
          listItemAt(live.current, block, event.target, {
            x: event.clientX,
            y: event.clientY,
          })?.id ?? block.id,
        )
        return
      }
      const controls = handleRef.current
      const target = controls?.dataset.actionBlockId
        ? blockElement(live.current, controls.dataset.actionBlockId)
        : null
      if (controls && target && controls.style.visibility !== 'hidden') {
        const bounds = target.getBoundingClientRect(),
          handle = controls.getBoundingClientRect()
        // The gap belongs to this block too, so the pointer can reach either button without a timer.
        if (
          event.clientX >= Math.min(bounds.left, handle.left) &&
          event.clientX <= Math.max(bounds.right, handle.right) &&
          event.clientY >= Math.min(bounds.top, handle.top) &&
          event.clientY <= Math.max(bounds.bottom, handle.bottom)
        )
          return
      }
      setHovered(null)
    }
    // The nearest editing boundary owns text-selection gestures, including the native touch callout.
    const editsText = (target: EventTarget | null) => {
      const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null
      return element?.closest('[contenteditable]')?.matches(EDITABLE) ?? false
    }
    const context = (event: MouseEvent) => {
      if (live.current.layout !== 'touch' || editsText(event.target)) return
      const block = topBlock(live.current, event.target)
      if (!block) return
      event.preventDefault()
      cancelPress()
      choose(block.id)
    }
    const down = (event: PointerEvent) => {
      if (event.pointerType !== 'touch' || live.current.layout !== 'touch')
        return
      if (
        event.target instanceof Element &&
        event.target.closest('input,textarea,button,a,[data-question-field]')
      )
        return
      const block = topBlock(live.current, event.target)
      if (!block) return
      // Explicit whole-block selection owns dragging; an ordinary text press remains native selection.
      if (editsText(event.target) && !live.current.state.selection?.nodes?.includes(block.id)) return
      press = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        block: block.id,
        selected: !!live.current.state.selection?.nodes?.includes(block.id),
      }
      cancelPress()
      timer = setTimeout(() => {
        choose(block.id)
        if (press) press.selected = true
      }, 450)
    }
    const autoScroll = () => {
      const current = dragging.current
      if (!current) return
      let host: HTMLElement | null = root.parentElement
      while (
        host &&
        !(
          host.scrollHeight > host.clientHeight &&
          /(auto|scroll)/u.test(getComputedStyle(host).overflowY)
        )
      )
        host = host.parentElement
      const rect = host?.getBoundingClientRect()
      const bottom = rect?.bottom ?? window.innerHeight,
        top = rect?.top ?? 0
      const amount =
        current.y < top + 36 ? -12 : current.y > bottom - 36 ? 12 : 0
      if (amount) {
        if (host) host.scrollTop += amount
        else window.scrollBy(0, amount)
      }
      frame = requestAnimationFrame(autoScroll)
    }
    const move = (event: PointerEvent) => {
      if (!press || press.id !== event.pointerId) return
      const distance = Math.hypot(
        event.clientX - press.x,
        event.clientY - press.y,
      )
      if (distance < 8 && !dragging.current) return
      cancelPress()
      if (!press.selected) {
        press = null
        return
      }
      event.preventDefault()
      event.stopImmediatePropagation()
      const block = live.current.state.blocks.find(
        (item) => item.id === press?.block,
      )
      const current = {
        blocks: live.current.state.selection?.nodes ?? [press.block],
        x: event.clientX,
        y: event.clientY,
        label: block ? plain(block) : '',
      }
      dragging.current = current
      setDrag(current)
      setOpen(false)
      updateDrop(event.clientX, event.clientY, event.target)
      if (!frame) frame = requestAnimationFrame(autoScroll)
    }
    const finish = (event: PointerEvent) => {
      if (!press || press.id !== event.pointerId) return
      cancelPress()
      if (
        dragging.current &&
        destination.current &&
        event.type !== 'pointercancel'
      ) {
        const target = destination.current
        dispatchDrop(live.current, dragging.current.blocks, target, event.altKey)
        event.preventDefault()
        event.stopImmediatePropagation()
      }
      press = null
      dragging.current = null
      destination.current = null
      setDrag(null)
      setDrop(null)
      cancelAnimationFrame(frame)
      frame = 0
    }
    const dragover = (event: DragEvent) => {
      if (!dragging.current) return
      event.preventDefault()
      event.stopImmediatePropagation()
      updateDrop(event.clientX, event.clientY, event.target)
    }
    const nativeDrop = (event: DragEvent) => {
      if (!dragging.current) return
      event.preventDefault()
      event.stopImmediatePropagation()
      const target = destination.current
      if (target) dispatchDrop(live.current, dragging.current.blocks, target, event.altKey)
      dragging.current = null
      destination.current = null
      setDrag(null)
      setDrop(null)
    }
    function updateDrop(x: number, y: number, target: EventTarget | null) {
      const api = live.current
      // A dragged list item lands among its sibling items only (cbe-ux-drag-reorder-list-item).
      const siblings =
        dragging.current?.blocks.length === 1
          ? itemSiblings(api.state.blocks, dragging.current.blocks[0])
          : undefined
      const pool = siblings ?? api.state.blocks
      const top = topBlock(api, target)
      const hit = siblings
        ? top && listItemAt(api, top, target, { x, y }, siblings)
        : top
      const candidates = pool.flatMap((block) => {
        const element = blockElement(api, block.id)
        return element
          ? [{ block, bounds: element.getBoundingClientRect() }]
          : []
      })
      const near =
        candidates.find((item) => item.block.id === hit?.id) ??
        candidates.toSorted(
          (a, b) =>
            Math.abs(y - (a.bounds.top + a.bounds.height / 2)) -
            Math.abs(y - (b.bounds.top + b.bounds.height / 2)),
        )[0]
      if (!near || dragging.current?.blocks.includes(near.block.id)) {
        destination.current = null
        setDrop(null)
        return
      }
      const edge = Math.min(72, near.bounds.width / 4)
      const side =
        api.layout === 'desktop' &&
        !siblings &&
        dragging.current?.blocks.length === 1 &&
        near.block.type !== 'cols'
          ? x <= near.bounds.left + edge
            ? 'left'
            : x >= near.bounds.right - edge
              ? 'right'
              : null
          : null
      const placement =
        side ??
        (y < near.bounds.top + near.bounds.height / 2 ? 'before' : 'after')
      const next: Drop = {
        target: near.block.id,
        placement,
        left: side === 'right' ? near.bounds.right : near.bounds.left,
        top: side
          ? near.bounds.top
          : placement === 'before'
            ? near.bounds.top
            : near.bounds.bottom,
        width: side ? 2 : near.bounds.width,
        ...(side && { height: near.bounds.height }),
      }
      destination.current = next
      setDrop(next)
      if (dragging.current) {
        dragging.current = { ...dragging.current, x, y }
        setDrag(dragging.current)
      }
    }
    const clearHover = () => {
      if (!open && !dragging.current) setHovered(null)
    }
    document.addEventListener('scroll', clearHover, true)
    window.addEventListener('resize', clearHover)
    document.addEventListener('mousemove', hover)
    root.addEventListener('contextmenu', context)
    root.addEventListener('pointerdown', down, true)
    document.addEventListener('pointermove', move, {
      capture: true,
      passive: false,
    })
    document.addEventListener('pointerup', finish, true)
    document.addEventListener('pointercancel', finish, true)
    root.addEventListener('dragover', dragover, true)
    root.addEventListener('drop', nativeDrop, true)
    return () => {
      cancelPress()
      cancelAnimationFrame(frame)
      document.removeEventListener('scroll', clearHover, true)
      window.removeEventListener('resize', clearHover)
      document.removeEventListener('mousemove', hover)
      root.removeEventListener('contextmenu', context)
      root.removeEventListener('pointerdown', down, true)
      document.removeEventListener('pointermove', move, true)
      document.removeEventListener('pointerup', finish, true)
      document.removeEventListener('pointercancel', finish, true)
      root.removeEventListener('dragover', dragover, true)
      root.removeEventListener('drop', nativeDrop, true)
    }
  }, [api.root, api.mode, handleRef, live, open])

  function targetSelection() {
    if (!block) return
    // Inside a field the handle's selection is only projected; the editor must actually select the block.
    if (
      editingApi.state.scope ||
      !api.state.selection?.nodes?.includes(block.id)
    )
      select(api, blockRange(api.state.blocks, block.id, block.id))
  }
  function replace(next: Block) {
    if (!block) return
    api.replaceBlock(block.id, { ...next, id: block.id })
  }
  const items: { title: string; run: () => void }[] = []
  if (block && api.onCopyBlockLink)
    items.push({
      title: 'Copy block link',
      run: () => {
        api.onCopyBlockLink?.((listItem ?? block).id)
      },
    })
  if (
    block?.type === 'question' &&
    ['sa', 'sc', 'mc', 'la'].includes(block.subtype)
  ) {
    for (const item of slashItems.filter((item) =>
      [
        'Short Answer',
        'Single Choice',
        'Multiple Choice',
        'Long Answer',
      ].includes(item.title),
    )) {
      items.push({
        title: item.title,
        run: () => {
          if (item.action.kind !== 'insert') return
          const next = item.action.build('')
          if (
            next.type !== 'question' ||
            next.subtype === 'sr' ||
            next.subtype === block.subtype
          )
            return
          replace(convertQuestionSubtype(block, next.subtype))
        },
      })
    }
  } else if (
    block &&
    ['row', 'container', 'list'].includes(blockClass(block))
  ) {
    // Text and its wrappers turn into other text blocks; an atom (a formula too) or a grid has no text to turn. The
    // handle's block turns as a whole where it stands: a callout, quote or toggle with its rows joined; a text block or
    // a list item through the caret's row (formatBlock).
    const whole =
      blockClass(block) === 'container' ||
      (block.type === 'list' && block.subtype === 'toggle')
    for (const item of slashItems.filter(
      (item) => item.action.kind === 'format',
    )) {
      items.push({
        title: item.title,
        run: () => {
          if (item.action.kind !== 'format') return
          if (whole) {
            replace(turnInto(block as Element, item.action.block))
            return
          }
          const target = listItem ?? block
          const focus = api.state.selection?.focus.blockId
          if (
            !api.state.selection?.nodes?.includes(block.id) &&
            (focusedBlock(api)?.id !== block.id ||
              (listItem && !(focus && findBlock([listItem], focus))))
          ) {
            const row = rows([target])[0]
            if (row) {
              const point = pointAt(row, 0)
              select(api, { anchor: point, focus: point })
            }
          }
          api.dispatch({ type: 'formatBlock', block: item.action.block })
        },
      })
    }
    items.push(
      {
        title: 'Callout',
        run: () => {
          if (listItem)
            api.dispatch({
              type: 'listItemAction',
              item: listItem.id,
              action: 'callout',
            })
          else
            replace({
              id: '',
              type: 'alert',
              icon: '💡',
              color: 'info',
              children:
                blockClass(block) === 'row'
                  ? [{ id: '', type: 'p', children: children(block) }]
                  : children(block),
            })
        },
      },
      {
        title: 'Toggle',
        run: () => {
          replace({
            id: '',
            type: 'list',
            subtype: 'toggle',
            children:
              block.type === 'list'
                ? block.children
                : [
                    {
                      id: '',
                      type: 'li',
                      children: [
                        { id: '', type: 'p', children: children(block) },
                      ],
                    },
                  ],
          })
        },
      },
    )
  }
  if (block?.type === 'cols') {
    for (let index = 0; index < block.children.length - 1; index++) {
      const left = block.children[index],
        right = block.children[index + 1]
      if (left.type !== 'col' || right.type !== 'col') continue
      for (const [title, direction] of [
        ['Widen', 1],
        ['Narrow', -1],
      ] as const)
        items.push({
          title: `${title} column ${index + 1}`,
          run: () => {
            const pair = {
              leftWidth: columnPercent(left.width) ?? 50,
              rightWidth: columnPercent(right.width) ?? 50,
            }
            api.dispatch({
              type: 'resizeColumns',
              left: left.id,
              right: right.id,
              ...keyResizeColumns(pair, direction),
            })
          },
        })
    }
  }
  if (block?.type === 'pre') {
    items.push(
      {
        title: 'Copy code',
        run: () => {
          // The code at the click: an IME session finishes on the click's capture, after this render.
          const live = findBlock(editor.getSnapshot().blocks, block.id)
          void navigator.clipboard.writeText(plain(live ?? block))
        },
      },
      {
        title: 'Wrap Code',
        run: () => {
          setWrapped((previous) =>
            previous.includes(block.id)
              ? previous.filter((id) => id !== block.id)
              : [...previous, block.id],
          )
        },
      },
    )
    for (const title of ['JavaScript', 'TypeScript', 'Mermaid', 'Plain Text'])
      items.push({
        title,
        run: () => {
          api.dispatch({
            type: 'setCodeLanguage',
            block: block.id,
            language: title === 'Plain Text' ? 'text' : title.toLowerCase(),
          })
        },
      })
    for (const language of codeHighlightingLanguages)
      items.push({
        title: `Language: ${language}`,
        run: () => {
          api.dispatch({ type: 'setCodeLanguage', block: block.id, language })
        },
      })
  }
  // A list item's handle acts on that item alone (Lexical $draggableNode).
  items.push(
    {
      title: 'Duplicate',
      run: () => {
        if (listItem)
          api.dispatch({
            type: 'listItemAction',
            item: listItem.id,
            action: 'duplicate',
          })
        else {
          targetSelection()
          api.dispatch({ type: 'duplicateBlock' })
        }
      },
    },
    {
      title: 'Delete',
      run: () => {
        if (listItem)
          api.dispatch({
            type: 'listItemAction',
            item: listItem.id,
            action: 'delete',
          })
        else {
          targetSelection()
          api.dispatch({ type: 'deleteContentBackward' })
        }
        setHovered(null)
      },
    },
  )
  if (api.layout === 'touch' && block) {
    for (const placement of ['before', 'after'] as const) {
      items.push(
        {
          title: placement === 'before' ? 'Insert above' : 'Insert below',
          run: () => {
            api.dispatch({
              type: 'insertBlockSibling',
              block: block.id,
              placement,
            })
          },
        },
        {
          title: placement === 'before' ? 'Move up' : 'Move down',
          run: () => {
            const index = api.state.blocks.indexOf(block)
            const target =
              api.state.blocks[index + (placement === 'before' ? -1 : 1)]
            if (target)
              api.dispatch({
                type: 'dropBlocks',
                blocks: [block.id],
                target: target.id,
                placement,
              })
          },
        },
      )
    }
  }
  // The page header is protected: no handle deletes, duplicates or moves it.
  if (
    api.mode !== 'edit' ||
    !block ||
    !element ||
    blockClass(block) === 'header'
  )
    return null
  if (hovered === undefined && !selected?.length && !plain(block)) return null
  const actionsId =
    api.testIds?.blockActions ?? 'content-blocks-editor-block-actions'
  const menuId = api.testIds?.blockMenu ?? 'content-blocks-editor-block-menu'
  const rect = handleBounds(element)
  const handle = handlePosition(
    rect,
    handleSize,
    Number(getComputedStyle(element).lineHeight.replace('px', '')) ||
      handleSize.height,
  )
  const order = [
    'Paragraph',
    'Heading 1',
    'Heading 2',
    'Heading 3',
    'Quote',
    'Callout',
    'Code Block',
    'Bullet List',
    'Numbered List',
    'Check List',
    'Toggle',
    'Delete',
    'Duplicate',
  ]
  items.sort(
    (a, b) =>
      (order.includes(a.title) ? order.indexOf(a.title) : 99) -
      (order.includes(b.title) ? order.indexOf(b.title) : 99),
  )
  return (
    <OverlayPortal api={api}>
      <Menu open={open} onOpenChange={setOpen}>
        <div ref={menu}>
          {api.layout === 'touch' ? (
            selected?.length && !drag ? (
              <MenuTrigger
                render={<IconButton />}
                aria-label="Block actions"
                data-testid="content-blocks-editor-selected-block-menu"
                className="border-border bg-surface-1 fixed z-50 rounded-md border"
                style={{ left: Math.max(0, rect.right - 30), top: rect.top }}
                aria-haspopup="menu"
                aria-expanded={open}
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
              >
                <EllipsisVerticalIcon />
                Block actions
              </MenuTrigger>
            ) : null
          ) : (
            <div
              ref={handleRef}
              data-testid={actionsId}
              data-action-block-id={(listItem ?? block).id}
              data-target-type={
                block.type === 'h'
                  ? 'heading'
                  : block.type === 'list'
                    ? 'content-blocks-listitem'
                    : ['img', 'video', 'audio', 'file'].includes(block.type)
                      ? 'media'
                      : block.type === 'p'
                        ? 'paragraph'
                        : block.type === 'pre'
                          ? 'code'
                          : block.type
              }
              data-edge-pinned={handle.edgePinned}
              data-dragging={!!drag}
              className="fixed z-[999] flex items-center gap-0.5"
              style={{
                left: handle.left,
                top: handle.top,
              }}
            >
              <IconButton
                className="border-border text-on-surface-secondary rounded-md"
                size="sm"
                tabIndex={-1}
                aria-label="Insert block"
                data-testid={`${actionsId}-plus`}
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onClick={(event) => {
                  api.dispatch({
                    type: 'insertBlockSibling',
                    block: block.id,
                    placement: event.altKey ? 'before' : 'after',
                  })
                  api.dispatch({ type: 'insertText', text: '/' })
                  api.root?.focus()
                }}
              >
                <PlusIcon />
                Insert block
              </IconButton>
              <MenuTrigger
                render={
                  <IconButton
                    className="border-border text-on-surface-secondary flex cursor-grab items-center justify-center rounded-md active:cursor-grabbing"
                    size="sm"
                    tooltipSideOffset={drag ? 12 : 11}
                  />
                }
                tabIndex={-1}
                aria-label="Block menu"
                data-testid={`${actionsId}-menu`}
                draggable
                onMouseDown={(event) => {
                  // Pressing must remain available to native drag-and-drop.
                  event.preventBaseUIHandler()
                }}
                onClick={(event) => {
                  event.preventBaseUIHandler()
                  setOpen((previous) => !previous)
                }}
                onDragStart={(event) => {
                  const source = listItem ?? block
                  const current = {
                    blocks: listItem
                      ? [source.id]
                      : draggedBlocks(api.state, block.id),
                    x: event.clientX,
                    y: event.clientY,
                    label: plain(source),
                  }
                  dragging.current = current
                  setDrag(current)
                  setOpen(false)
                  event.dataTransfer.setData(
                    dragMime,
                    JSON.stringify(current.blocks),
                  )
                  event.dataTransfer.effectAllowed = 'move'
                }}
                onDragEnd={() => {
                  dragging.current = null
                  destination.current = null
                  setDrag(null)
                  setDrop(null)
                }}
              >
                <GripVerticalIcon />
                Drag or open block menu
              </MenuTrigger>
            </div>
          )}
          <MenuPopup
            data-core-overlay="true"
            ref={menu}
            align="start"
            side="right"
            sideOffset={8}
            small
            className="w-56 shadow-xl"
            data-testid={menuId}
          >
            {items.map((item) => (
              <Fragment key={item.title}>
                {item.title === 'Delete' && <MenuSeparator />}
                <MenuItem
                  variant={item.title === 'Delete' ? 'error' : 'default'}
                  data-testid={`${menuId}-${segment(item.title)}`}
                  onMouseDown={(event) => {
                    event.preventDefault()
                  }}
                  onClick={() => {
                    item.run()
                    setOpen(false)
                    api.root?.focus()
                  }}
                >
                  {blockMenuIcon(item.title)}
                  {item.title}
                </MenuItem>
              </Fragment>
            ))}
          </MenuPopup>
          {drag && api.layout === 'touch' && (
            <div
              data-testid={`${actionsId}-drag-preview`}
              className={`${panelClass} pointer-events-none fixed max-w-64 truncate opacity-80`}
              style={{ left: drag.x + 12, top: drag.y + 12 }}
            >
              {drag.label || 'Move block'}
            </div>
          )}
          {drop && (
            <div
              aria-label="Drop position"
              role="separator"
              data-testid={`${actionsId}-drop-indicator`}
              data-placement={drop.placement}
              className="bg-primary pointer-events-none fixed z-[998] rounded-full shadow-sm"
              style={{
                left: drop.left,
                top: drop.top,
                width: drop.width,
                height: drop.height ?? 2,
                transform: drop.height
                  ? 'translateX(-1px)'
                  : 'translateY(-1px)',
              }}
            />
          )}
        </div>
      </Menu>
    </OverlayPortal>
  )
}

/** The nearest list item of `top` under `target`; with `pool`, the nearest block of `pool` instead. */
function listItemAt(
  api: Pick<OverlayApi, 'root'>,
  top: Block,
  target: EventTarget | null,
  point?: { x: number; y: number },
  pool?: readonly Block[],
): Block | undefined {
  // Native outside markers hit the list's padding rather than the li itself.
  if (point && target instanceof HTMLElement && target.matches('ul,ol')) {
    for (const item of target.children) {
      if (!(item instanceof HTMLElement) || item.tagName !== 'LI') continue
      const bounds = item.getBoundingClientRect()
      if (
        point.x >= bounds.left ||
        point.y < bounds.top ||
        point.y >= bounds.bottom
      )
        continue
      const found = pool
        ? pool.find((block) => block.id === item.dataset.blockId)
        : findBlock([top], item.dataset.blockId ?? '')
      if (found) return found
    }
  }
  for (
    let element = target instanceof Element ? target : null;
    element && element !== api.root;
    element = element.parentElement
  ) {
    const id =
      element instanceof HTMLElement ? element.dataset.blockId : undefined
    const found = id
      ? pool
        ? pool.find((block) => block.id === id)
        : findBlock([top], id)
      : undefined
    if (found && (pool || found.type === 'li')) return found
  }
  return undefined
}

/** The items of the list holding list item `id`; undefined when `id` is no list item. */
function itemSiblings(
  items: readonly Block[],
  id: string,
): readonly Block[] | undefined {
  for (const block of items) {
    const nested = children(block)
    if (block.type === 'list' && nested.some((child) => child.id === id))
      return nested
    const found = itemSiblings(nested, id)
    if (found) return found
  }
  return undefined
}

/** Compose core intents; both original blocks move into new columns with their identities intact. `copy` (Alt held,
 * Notion "Duplicate by dragging") drops copies before or after the target; a side drop always moves. */
function dispatchDrop(api: OverlayApi, sources: string[], target: Drop, copy: boolean) {
  if (target.placement !== 'left' && target.placement !== 'right') {
    api.dispatch({
      type: 'dropBlocks',
      blocks: sources,
      target: target.target,
      placement: target.placement,
      ...(copy && { copy: true }),
    })
    return
  }
  const group = `view:columns:${ulid()}`
  const dispatch = (intent: EditorIntent) =>
    api.dispatchWithHistory(intent, group)
  const previousIds = new Set(api.state.blocks.map((block) => block.id))
  dispatch({
    type: 'insertBlockSibling',
    block: target.target,
    placement: 'before',
  })
  const result = dispatch({
    type: 'insertFromPaste',
    md: '',
    fragment: [
      {
        id: '',
        type: 'cols',
        children: [
          { id: '', type: 'col', width: 50, children: [] },
          { id: '', type: 'col', width: 50, children: [] },
        ],
      },
    ],
  })
  const columns = result.state.blocks.find(
    (block) => block.type === 'cols' && !previousIds.has(block.id),
  )
  if (columns?.type !== 'cols')
    throw new Error('Column drop did not create columns')
  const sourceIndex = target.placement === 'left' ? 0 : 1
  dispatch({
    type: 'dropBlocks',
    blocks: sources,
    target: columns.children[sourceIndex].id,
    placement: 'inside',
  })
  dispatch({
    type: 'dropBlocks',
    blocks: [target.target],
    target: columns.children[1 - sourceIndex].id,
    placement: 'inside',
  })
}

function blockMenuIcon(title: string) {
  const className = 'text-on-surface-secondary size-4 shrink-0'
  if (title === 'Delete') return <Trash2Icon className={className} />
  if (title === 'Duplicate') return <CopyIcon className={className} />
  if (title === 'Paragraph') return <FileTextIcon className={className} />
  if (title.startsWith('Heading')) return <HashIcon className={className} />
  if (title === 'Quote') return <MessageSquareIcon className={className} />
  if (title === 'Callout') return <CircleAlertIcon className={className} />
  if (title === 'Toggle') return <ChevronRightIcon className={className} />
  if (title === 'Code Block' || title === 'Wrap Code') {
    return <CodeXmlIcon className={className} />
  }
  if (title === 'Check List') return <ListTodoIcon className={className} />
  return <ListIcon className={className} />
}

/** Outside markers occupy the list gutter, which is absent from an li's border box. */
function handleBounds(element: HTMLElement): DOMRect {
  const rect = element.getBoundingClientRect()
  const list = element.parentElement
  if (element.tagName !== 'LI' || !list) return rect
  const style = getComputedStyle(element)
  if (style.listStylePosition !== 'outside' || style.listStyleType === 'none')
    return rect
  let left = Math.min(rect.left, list.getBoundingClientRect().left)
  if (list instanceof HTMLOListElement && style.listStyleType === 'decimal') {
    const context = element.ownerDocument
      .createElement('canvas')
      .getContext('2d')
    if (context) {
      context.font = getComputedStyle(element, '::marker').font || style.font
      const number = list.start + [...list.children].indexOf(element)
      left = Math.min(
        left,
        rect.left - context.measureText(`${number}. `).width,
      )
    }
  }
  return new DOMRect(left, rect.top, rect.right - left, rect.height)
}

function handlePosition(
  rect: DOMRect,
  { width, height }: { width: number; height: number },
  lineHeight: number,
): {
  left: number
  top: number
  edgePinned: boolean
} {
  const gutterLeft = rect.left - width - 4
  const dockRight = gutterLeft < 4 && rect.top < height + 4 * 2
  const desiredLeft = dockRight ? rect.right - width : gutterLeft
  const maxLeft = Math.max(4, window.innerWidth - width - 4)
  const left = Math.min(Math.max(desiredLeft, 4), maxLeft)
  const dockAbove = left !== desiredLeft && rect.top >= height + 4 * 2
  const desiredTop = dockAbove
    ? rect.top - height - 4
    : rect.top + (Math.min(lineHeight, rect.height) - height) / 2
  const maxTop = Math.max(4, window.innerHeight - height - 4)
  const top = Math.min(Math.max(desiredTop, 4), maxTop)
  return {
    left,
    top,
    edgePinned: dockRight || left !== desiredLeft || top !== desiredTop,
  }
}
